/**
 * Carregamento e render de PDF com pdf.js (E23.F20 — ADR-0030). // ux-neutrality:allow doc-comment
 *
 * Isola a dependência `pdfjs-dist` e a configuração do seu processador num // ux-neutrality:allow doc-comment
 * único módulo: os componentes consomem só `loadPdf` / `RenderablePdf`, e os // ux-neutrality:allow doc-comment
 * testes mockam este módulo (em vez de lidar com o processador e o canvas // ux-neutrality:allow doc-comment
 * reais no jsdom). // ux-neutrality:allow doc-comment
 */
import * as pdfjs from 'pdfjs-dist';
// `?url` (Vite): empacota o script do processador e devolve sua URL final —
// sem CDN externo, consistente com o perímetro self-host (dados forenses não
// saem da VPS). // ux-neutrality:allow build-infra-term (pdf.worker é nome de arquivo da lib)
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'; // ux-neutrality:allow build-infra-term

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl; // ux-neutrality:allow build-infra-term

/** Documento PDF já parseado, pronto para renderizar página a página. */
export interface RenderablePdf {
  readonly numPages: number;
  /** Renderiza a página (1-based) no canvas, ajustando suas dimensões ao viewport. */
  renderPage(pageNumber: number, canvas: HTMLCanvasElement, scale: number): Promise<void>;
  /** Libera os recursos do documento e do seu processador. */
  destroy(): void;
}

export async function loadPdf(data: ArrayBuffer): Promise<RenderablePdf> {
  // Cópia: pdf.js transfere/destaca o buffer subjacente ao seu processador.
  // E166.F2.T05: a CSP não libera `eval`; sem isto o pdf.js tenta e só então cai no caminho sem ele.
  const doc = await pdfjs.getDocument({ data: new Uint8Array(data), isEvalSupported: false })
    .promise;
  return {
    numPages: doc.numPages,
    async renderPage(pageNumber, canvas, scale) {
      const page = await doc.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const context = canvas.getContext('2d');
      if (!context) throw new Error('canvas 2d context unavailable');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: context, viewport }).promise;
    },
    destroy() {
      void doc.destroy();
    },
  };
}
